"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useState } from "react";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { Tooltip } from "react-tooltip";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useMutation } from "@tanstack/react-query";
import { commonApiDeleteWithBody, commonApiPost } from "@/services/api/common-api";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import type { ApiIdentitySubscriptionActions } from "@/generated/models/ApiIdentitySubscriptionActions";
import { ApiIdentitySubscriptionTargetAction } from "@/generated/models/ApiIdentitySubscriptionTargetAction";
enum FOLLOW_STATE {
  FOLLOWING = "FOLLOWING",
  NOT_FOLLOWING = "NOT_FOLLOWING",
}
export default function WaveDropFollowAuthor(t0) {
  const $ = _c(50);
  const {
    drop
  } = t0;
  const followState = drop.author?.subscribed_actions?.length ? FOLLOW_STATE.FOLLOWING : FOLLOW_STATE.NOT_FOLLOWING;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg className="tw-flex-shrink-0 tw-size-4 tw-transition tw-ease-out tw-duration-300" width="17" height="15" viewBox="0 0 17 15" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M14.7953 0.853403L5.24867 10.0667L2.71534 7.36007C2.24867 6.92007 1.51534 6.8934 0.982005 7.26674C0.462005 7.6534 0.315338 8.3334 0.635338 8.88007L3.63534 13.7601C3.92867 14.2134 4.43534 14.4934 5.00867 14.4934C5.55534 14.4934 6.07534 14.2134 6.36867 13.7601C6.84867 13.1334 16.0087 2.2134 16.0087 2.2134C17.2087 0.986737 15.7553 -0.093263 14.7953 0.84007V0.853403Z" fill="currentColor" /></svg>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [FOLLOW_STATE.FOLLOWING]: t1,
      [FOLLOW_STATE.NOT_FOLLOWING]: <svg className="tw-flex-shrink-0 tw-size-4 tw-transition tw-ease-out tw-duration-300" width="17" height="15" viewBox="0 0 17 15" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M14.7953 0.853403L5.24867 10.0667L2.71534 7.36007C2.24867 6.92007 1.51534 6.8934 0.982005 7.26674C0.462005 7.6534 0.315338 8.3334 0.635338 8.88007L3.63534 13.7601C3.92867 14.2134 4.43534 14.4934 5.00867 14.4934C5.55534 14.4934 6.07534 14.2134 6.36867 13.7601C6.84867 13.1334 16.0087 2.2134 16.0087 2.2134C17.2087 0.986737 15.7553 -0.093263 14.7953 0.84007V0.853403Z" fill="currentColor" /></svg>
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const components = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      [FOLLOW_STATE.FOLLOWING]: "tw-text-primary-500 hover:tw-text-primary-400",
      [FOLLOW_STATE.NOT_FOLLOWING]: "tw-text-iron-500 hover:tw-text-iron-50"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const classes = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      [FOLLOW_STATE.FOLLOWING]: "Following",
      [FOLLOW_STATE.NOT_FOLLOWING]: "Follow"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const tooltipText = t4;
  const {
    setToast,
    requestAuth
  } = useContext(AuthContext);
  const {
    invalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const [mutating, setMutating] = useState(false);
  let t5;
  if ($[4] !== drop.author.id) {
    t5 = async () => {
      await commonApiPost({
        endpoint: `identities/${drop.author.id}/subscriptions`,
        body: {
          actions: Object.values(ApiIdentitySubscriptionTargetAction).filter(_temp)
        }
      });
    };
    $[4] = drop.author.id;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== invalidateDrops) {
    t6 = () => {
      invalidateDrops();
    };
    $[6] = invalidateDrops;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== setToast) {
    t7 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[8] = setToast;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMutating(false);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t9 = {
      mutationFn: t5,
      onSuccess: t6,
      onError: t7,
      onSettled: t8
    };
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const followMutation = useMutation(t9);
  let t10;
  if ($[15] !== drop.author.id) {
    t10 = async () => {
      await commonApiDeleteWithBody({
        endpoint: `identities/${drop.author.id}/subscriptions`,
        body: {
          actions: Object.values(ApiIdentitySubscriptionTargetAction).filter(_temp2)
        }
      });
    };
    $[15] = drop.author.id;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== invalidateDrops) {
    t11 = () => {
      invalidateDrops();
    };
    $[17] = invalidateDrops;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== setToast) {
    t12 = error_0 => {
      setToast({
        message: error_0 as unknown as string,
        type: "error"
      });
    };
    $[19] = setToast;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setMutating(false);
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t12) {
    t14 = {
      mutationFn: t10,
      onSuccess: t11,
      onError: t12,
      onSettled: t13
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const unFollowMutation = useMutation(t14);
  let t15;
  if ($[26] !== followMutation || $[27] !== followState || $[28] !== requestAuth || $[29] !== unFollowMutation) {
    const onFollow = async () => {
      setMutating(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setMutating(false);
        return;
      }
      if (followState === FOLLOW_STATE.FOLLOWING) {
        await unFollowMutation.mutateAsync();
        return;
      }
      await followMutation.mutateAsync();
    };
    t15 = e => {
      e.stopPropagation();
      onFollow();
    };
    $[26] = followMutation;
    $[27] = followState;
    $[28] = requestAuth;
    $[29] = unFollowMutation;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = `${classes[followState]} tw-text-iron-500 icon tw-px-2 tw-h-full tw-group tw-bg-transparent tw-rounded-full tw-border-0 tw-inline-flex tw-items-center tw-gap-x-1.5 tw-text-xs tw-leading-5 tw-font-medium tw-transition tw-ease-out tw-duration-300`;
  const t17 = followState === FOLLOW_STATE.FOLLOWING ? `Unfollow ${drop.author.handle}` : `Follow ${drop.author.handle}`;
  const t18 = `follow-author-${drop.id}`;
  let t19;
  if ($[31] !== followState || $[32] !== mutating) {
    t19 = mutating ? <CircleLoader size={CircleLoaderSize.SMALL} /> : components[followState];
    $[31] = followState;
    $[32] = mutating;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== mutating || $[35] !== t15 || $[36] !== t16 || $[37] !== t17 || $[38] !== t18 || $[39] !== t19) {
    t20 = <button onClick={t15} disabled={mutating} className={t16} aria-label={t17} data-tooltip-id={t18}>{t19}</button>;
    $[34] = mutating;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = `follow-author-${drop.id}`;
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    };
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = tooltipText[followState];
  let t24;
  if ($[42] !== t23) {
    t24 = <span className="tw-text-xs">{t23}</span>;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t21 || $[45] !== t24) {
    t25 = <Tooltip id={t21} place="top" offset={8} opacity={1} style={t22}>{t24}</Tooltip>;
    $[44] = t21;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t20 || $[48] !== t25) {
    t26 = <>{t20}{t25}</>;
    $[47] = t20;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  return t26;
}
function _temp2(i_0) {
  return i_0 !== ApiIdentitySubscriptionTargetAction.DropVoted;
}
function _temp(i) {
  return i !== ApiIdentitySubscriptionTargetAction.DropVoted;
}